import { useEffect, useState } from 'react';
import produce from 'immer';
import { Toast } from '../services/toast';
import { ModalParams } from '../ui/Modal/ModalBody';
import { DropperProps } from '../ui/Popover/PopoverBody';
import { TaskProps } from '../services/task';
import { SkillProps } from '../services/skill';

export const stores = createStores({
    UI: createStores({
        toast: createStores({
            queue: [] as Toast[],
        }),
        modal: undefined as any as ModalParams,
        dropper: undefined as any as DropperProps,
    }),
    clientConfig: 'prjam',
    tasks: [] as TaskProps[],
    tasksByName: {} as { [name: TaskProps['name']]: TaskProps },
    skills: [] as SkillProps[],
});

window.stores = stores;

export function useStore<T>(store: Store<T>): [T, StoreSetter<T>] {
    const [state, setState] = useState<T>(store.state);
    store.subscribe(setState);
    //eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => () => store.unsubscribe(setState), []);

    return [state, store.set];
}

export function getStoreValue<T>(store: Store<T>) {
    return store.state;
}

type stateSubscriber<T = unknown> = (state: T) => void;
export interface Store<T = unknown> {
    _store: true;
    state: T;
    set: StoreSetter<T>;
    reset: () => void;
    subscribe: (setState: stateSubscriber<T>) => Set<stateSubscriber<T>>;
    unsubscribe: (setState: stateSubscriber<T>) => void;
}

declare global {
    interface Window {
        traceStore: Store;
        watchStore: Store;
        __REDUX_DEVTOOLS_EXTENSION__?: any;
        // eslint-disable-next-line no-undef
        stores: typeof stores;
    }
}

export function createGlobalStore<T>(initialState: T, storeName: string) {
    const subscriptions = new Set<stateSubscriber<T>>();

    const store = {
        _store: true,
    } as Store<T>;

    store.state = initialState;

    store.set = (updateState: T | ((draft: T) => void)) => {
        const newState = updateState instanceof Function ? produce(store.state, updateState) : updateState;
        store.state = newState;
        subscriptions.forEach((setState) => setState(newState));
    };
    store.reset = () => store.set(initialState);

    store.subscribe = (setState) => subscriptions.add(setState);
    store.unsubscribe = (setState) => subscriptions.delete(setState);
    return store;
}

export function isStore(item: any): item is Store {
    return Boolean(item && item._store);
}

type storeType<T> = [T] extends [{ _store: boolean }]
    ? T extends Store<T>
    ? Store<T>
    : T & { _store: boolean }
    : Store<T>;

// eslint-disable-next-line @typescript-eslint/ban-types
export function createStores<T extends Object>(object: T) {
    return {
        ...Object.keys(object).reduce((stores, key) => {
            (stores as any)[key] = isStore((object as any)[key]) ? (object as any)[key] : createGlobalStore((object as any)[key], key);
            return stores;
        }, {} as { [K in keyof T]: storeType<T[K]> }),
        _store: true,
    };
}

export interface StoreSetter<T> {
    (updateState: T): void;
    (updateState: (storeDraft: T) => void): void;
}